import React, { useState, useEffect } from 'react';
import {
    MapPin, Plus, Phone, Navigation, Building,
    CheckCircle2, MoreVertical, Trash2, Home, Users
} from 'lucide-react';
import api from '../api/axiosInstance';

const BranchManagement = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        branch_name: '',
        address: '',
        contact_number: ''
    });

    useEffect(() => {
        fetchBranches();
    }, []);

    const fetchBranches = async () => {
        try {
            const response = await api.get('get_branches.php');
            if (response.data.status === "success") {
                setBranches(response.data.branches);
            }
        } catch (err) {
            console.error("Error fetching branches");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBranch = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('create_branch.php', formData);
            if (response.data.status === "success") {
                setIsModalOpen(false);
                setFormData({ branch_name: '', address: '', contact_number: '' });
                fetchBranches();
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to create branch");
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Branch Network</h1>
                    <p className="text-slate-500 font-medium italic">Manage workshops, offices, and showrooms.</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-indigo-100 transition-all flex items-center gap-2"
                >
                    <Plus size={18} /> New Location
                </button>
            </div>

            {/* Branch Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {branches.map((branch) => (
                    <div key={branch.id} className="bg-white rounded-[40px] border border-slate-100 p-8 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-slate-50 text-slate-400 group-hover:bg-indigo-600 group-hover:text-white flex items-center justify-center transition-all border border-slate-100 group-hover:border-indigo-500">
                                    <Building size={24} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-black text-slate-900">{branch.branch_name}</h3>
                                        {branch.is_main_branch && (
                                            <span className="bg-emerald-50 text-emerald-600 text-[9px] font-black uppercase px-2 py-1 rounded-lg border border-emerald-100 flex items-center gap-1">
                                                <Home size={10} /> Primary
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-400 mt-1">
                                        <MapPin size={14} />
                                        <p className="text-xs font-medium truncate max-w-[200px]">{branch.address || 'No address set'}</p>
                                    </div>
                                </div>
                            </div>
                            <button className="p-2 text-slate-300 hover:text-rose-500 transition-colors">
                                <Trash2 size={20} />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-slate-50">
                            <div className="bg-slate-50 rounded-2xl p-4">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                                    <Phone size={10} /> Contact
                                </p>
                                <p className="text-sm font-bold text-slate-700">{branch.contact_number || 'N/A'}</p>
                            </div>
                            <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-50">
                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                                    <Users size={10} /> Active Staff
                                </p>
                                <p className="text-sm font-bold text-indigo-600">{branch.staff_count || 0} Members</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- ADD BRANCH MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setIsModalOpen(false)}></div>
                    <div className="bg-white w-full max-w-lg rounded-[48px] shadow-2xl relative z-10 p-12 border border-white">
                        <h2 className="text-2xl font-black text-slate-900 mb-8 italic">Add New Location</h2>
                        <form onSubmit={handleCreateBranch} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-4">Location Name</label>
                                <input
                                    type="text" placeholder="e.g., Virar West Workshop" required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-6 outline-none focus:ring-4 focus:ring-indigo-50 transition-all font-medium"
                                    onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-4">Full Address</label>
                                <textarea
                                    placeholder="Street, Area, City, Pincode" required rows="3"
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-6 outline-none focus:ring-4 focus:ring-indigo-50 transition-all font-medium"
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                ></textarea>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-4">Contact Number</label>
                                <input
                                    type="text" placeholder="+91 00000 00000" required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-6 outline-none focus:ring-4 focus:ring-indigo-50 transition-all font-medium"
                                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                                />
                            </div>
                            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-5 rounded-3xl transition-all shadow-xl shadow-indigo-100 mt-4 flex items-center justify-center gap-2">
                                <CheckCircle2 size={20} /> Deploy Branch
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchManagement;